import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { Heading } from '../layout';
import SalesTable3 from './SalesTable3';
import QuotationsTable3 from './QuotationsTable3';
import SalesDetailModal from './SalesDetailModal';
import PeriodSummaryTable from './PeriodSummaryTable';
import RegionSegmentStackedChart, {
  PRODUCT_LINE_DIMENSION,
  REGION_FILTER_DIMENSION,
  SEGMENT_STACK_DIMENSION,
  TCOAT_PRODUCT_DIMENSION,
  normalizeCommercialSegment,
} from './RegionSegmentStackedChart';
import MonthlyDistributionChart from './MonthlyDistributionChart';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';
import { useUIColors } from '../../hooks/useTheme';
import {
  getSaleDocumentCandidates,
  setRegisterSaleLink,
} from '../../services/treasuryAPI';
import { buildLinkItemNotes, documentAmountMatchesSaleTotal, getAllSaleItemSelection, getBillingDocumentAmount } from './saleBillingDocumentUtils';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  collectDates,
  getAvailableYearsFromDates,
  getMonthsInRange,
  resolvePeriodSelection,
  isDateInRange,
  isDateInSelectedPeriod,
  withSalesPeriodFutureCloseBuffer,
  parseDateSafe,
  periodSelectStyle,
  periodLabelStyle,
} from './periodSummaryUtils';
import {
  CHART_METRIC_AMOUNT,
  CHART_METRIC_COUNT,
  CHART_METRIC_GALLONS,
  CHART_METRIC_ITEMS,
  resolvePricePerGallon,
  resolveTcoatLineGallons,
} from './chartFilterUtils';

const SUMMARY_METRIC_OPTIONS = [
  { value: 'count', label: 'Cantidad de ventas' },
  { value: 'amount', label: 'Montos vendidos' },
  { value: 'gallons', label: 'Galones T-COAT' },
];

const SUMMARY_SEGMENT_ORDER = ['B2G', 'B2B', 'B2C', 'B2B2C', 'Sin Asignar'];

const SALE_CHART_VALUE_METRICS = [
  CHART_METRIC_COUNT,
  CHART_METRIC_ITEMS,
  CHART_METRIC_AMOUNT,
  CHART_METRIC_GALLONS,
];

const SALE_CHART_PRODUCT_FILTERS = [PRODUCT_LINE_DIMENSION, TCOAT_PRODUCT_DIMENSION];

/** Fecha de cierre; si no hay, usa fecha de cotización para no ocultar adjudicadas. */
const getSaleAnchorDate = (sale) => sale?.deal_close_date || sale?.quotation_date || '';

const isTcoatQuotationItem = (item = {}, paintSkuSet = null, upsellingSkuSet = null) => {
  const sku = String(item.product_sku || '').trim();
  const skuKey = sku.toUpperCase();
  const name = String(item.item_name || item.product_name || '');
  if (skuKey && upsellingSkuSet && upsellingSkuSet.has(skuKey)) return false;
  if (skuKey && paintSkuSet && paintSkuSet.has(skuKey)) return true;
  if (/t[\s-]?coat/i.test(name)) return true;
  if (paintSkuSet || upsellingSkuSet) return false;
  return Boolean(skuKey);
};

const resolveQuotationLineNet = (item = {}) => {
  const lineTotal = Number(item.line_total);
  if (Number.isFinite(lineTotal)) return lineTotal;
  const qty = Number(item.quantity || 0);
  const unitPrice = Number(item.unit_price || 0);
  const discount = Math.max(Math.min(Number(item.discount_percent || 0), 100), 0);
  if (!Number.isFinite(qty) || !Number.isFinite(unitPrice)) return 0;
  return qty * unitPrice * (1 - discount / 100);
};

const formatSummaryCount = (value) => Number(value || 0).toLocaleString('es-CL');

const formatSummaryMoney = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '$0';
  return `$${Math.round(n).toLocaleString('es-CL')}`;
};

/** Total neto → total con IVA (19%). */
const grossAmountConIva = (netTotal) => {
  const t = Number(netTotal || 0);
  if (!Number.isFinite(t) || t === 0) return 0;
  return Math.round(t * 1.19 * 100) / 100;
};

const isClpLikeCurrency = (currency) => {
  const c = String(currency || 'CLP').trim().toUpperCase();
  return !c || c === 'CLP' || c === 'PESOS';
};

const SALE_STATUS_ORDER = [
  'En Preparación',
  'En Ruta',
  'Recepcionado',
  'Facturado Parcial',
  'Facturado',
  'Pagado',
];

const SALE_CHART_STATUS_ORDER = ['Adjudicada', ...SALE_STATUS_ORDER];

const SALE_STATUS_CHART_COLORS = {
  Adjudicada: '#9333ea',
  'En Preparación': '#ca8a04',
  'En Ruta': '#c2410c',
  Recepcionado: '#0f766e',
  'Facturado Parcial': '#d97706',
  Facturado: '#1d4ed8',
  Pagado: '#15803d',
};

const normalizeSaleStatus = (status = '') => {
  const value = String(status || '').trim();
  if (SALE_STATUS_ORDER.includes(value)) return value;
  return 'En Preparación';
};

const isVendidaSale = (sale) => String(sale?.quotation_status || '').trim() === 'Vendida';
const isAdjudicadaSale = (sale) => String(sale?.quotation_status || '').trim() === 'Adjudicada';

const resolveChartSaleStatus = (sale) => {
  if (isAdjudicadaSale(sale)) return 'Adjudicada';
  return normalizeSaleStatus(sale?.sale_status);
};

const normalizeQuotationStatus = (status = '') => {
  const value = String(status || '').trim();
  if (value === 'Aprobada') return 'Adjudicada';
  if (value === 'Enviada') return 'Emitida';
  return value || 'Borrador';
};

const getQuotationAnchorDate = (quotation) =>
  quotation?.quotation_date || quotation?.closing_date || '';

const normalizeSalePatch = (sale) => {
  if (!sale?.id) return null;
  const closingDate = sale.quotation_closing_date ?? sale.deal_close_date ?? null;
  return {
    ...sale,
    deal_close_date: sale.deal_close_date ?? closingDate,
    quotation_closing_date: sale.quotation_closing_date ?? closingDate,
  };
};

const PaintSalesWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [salesData, setSalesData] = useState([]);
  const [quotationItemsData, setQuotationItemsData] = useState([]);
  const [paintSkuSet, setPaintSkuSet] = useState(() => new Set());
  const [upsellingSkuSet, setUpsellingSkuSet] = useState(() => new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isItemsLoading, setIsItemsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleDetail, setSaleDetail] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [isSavingDetail, setIsSavingDetail] = useState(false);
  const [documentCandidates, setDocumentCandidates] = useState([]);
  const [documentCandidatesError, setDocumentCandidatesError] = useState('');
  const [isLoadingDocumentCandidates, setIsLoadingDocumentCandidates] = useState(false);
  const [isLinkingDocument, setIsLinkingDocument] = useState(false);
  const [quotationsData, setQuotationsData] = useState([]);
  const [isAddingDelivery, setIsAddingDelivery] = useState(false);

  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState('all');
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [chartsCollapsed, setChartsCollapsed] = useState(false);
  const [summaryMetric, setSummaryMetric] = useState('amount');
  const [summarySegmentVisibility, setSummarySegmentVisibility] = useState({});
  const [listTab, setListTab] = useState('sales');

  const salesLoadSeqRef = useRef(0);
  const detailSessionDirtyRef = useRef(false);

  const loadSales = async () => {
    const loadSeq = ++salesLoadSeqRef.current;
    setIsLoading(true);
    setIsItemsLoading(true);
    setError('');
    try {
      // Primero lo necesario para tabla/resumen por monto.
      const salesResponse = await authFetch(`${API_BASE_URL}/api/quotations/sales`);
      if (!salesResponse.ok) throw new Error(`HTTP ${salesResponse.status}`);
      const rawData = await salesResponse.json();
      if (loadSeq !== salesLoadSeqRef.current) return;
      setSalesData(Array.isArray(rawData) ? rawData : []);
      setIsLoading(false);
    } catch (fetchError) {
      if (loadSeq !== salesLoadSeqRef.current) return;
      console.error('Error loading sales:', fetchError);
      setSalesData([]);
      setQuotationItemsData([]);
      setQuotationsData([]);
      setPaintSkuSet(new Set());
      setUpsellingSkuSet(new Set());
      setError('No se pudieron cargar las ventas.');
      setIsLoading(false);
      setIsItemsLoading(false);
      return;
    }

    // Segundo plano: ítems (gráficos/galones), catálogos SKU y cotizaciones auxiliares.
    try {
      const [itemsResponse, productsResponse, upsellingResponse, quotationsResponse] =
        await Promise.all([
          authFetch(`${API_BASE_URL}/api/quotations/sales/items`),
          authFetch(`${API_BASE_URL}/api/quotations/products`),
          authFetch(`${API_BASE_URL}/api/quotations/upselling-products`),
          authFetch(`${API_BASE_URL}/api/quotations/`),
        ]);
      if (loadSeq !== salesLoadSeqRef.current) return;

      if (itemsResponse.ok) {
        const itemsRaw = await itemsResponse.json();
        if (loadSeq !== salesLoadSeqRef.current) return;
        setQuotationItemsData(Array.isArray(itemsRaw) ? itemsRaw : []);
      } else {
        setQuotationItemsData([]);
      }
      if (quotationsResponse.ok) {
        const qRaw = await quotationsResponse.json();
        if (loadSeq !== salesLoadSeqRef.current) return;
        setQuotationsData(Array.isArray(qRaw) ? qRaw : []);
      } else {
        setQuotationsData([]);
      }
      if (productsResponse.ok) {
        const productsRaw = await productsResponse.json();
        if (loadSeq !== salesLoadSeqRef.current) return;
        const paintSkus = new Set(
          (Array.isArray(productsRaw) ? productsRaw : [])
            .map((row) => String(row.sku || '').trim().toUpperCase())
            .filter(Boolean)
        );
        setPaintSkuSet(paintSkus);
      } else {
        setPaintSkuSet(new Set());
      }
      if (upsellingResponse.ok) {
        const upsRaw = await upsellingResponse.json();
        if (loadSeq !== salesLoadSeqRef.current) return;
        const upsSkus = new Set();
        (Array.isArray(upsRaw) ? upsRaw : []).forEach((row) => {
          [row.upselling_code, row.legacy_code, row.legacy_code2].forEach((code) => {
            const key = String(code || '').trim().toUpperCase();
            if (key) upsSkus.add(key);
          });
        });
        setUpsellingSkuSet(upsSkus);
      } else {
        setUpsellingSkuSet(new Set());
      }
    } catch (bgError) {
      if (loadSeq !== salesLoadSeqRef.current) return;
      console.error('Error loading sales detail (items/catalogs):', bgError);
      setQuotationItemsData([]);
      setQuotationsData([]);
      setPaintSkuSet(new Set());
      setUpsellingSkuSet(new Set());
    } finally {
      if (loadSeq === salesLoadSeqRef.current) setIsItemsLoading(false);
    }
  };

  useEffect(() => {
    loadSales();
  }, []);

  const applySaleSnapshot = (updatedSale) => {
    const patch = normalizeSalePatch(updatedSale);
    if (!patch) return;
    const saleId = Number(patch.id);
    setSalesData((prev) =>
      (prev || []).map((row) =>
        Number(row.id) === saleId ? { ...row, ...patch } : row
      )
    );
    setSelectedSale((prev) =>
      prev && Number(prev.id) === saleId ? { ...prev, ...patch } : prev
    );
    setSaleDetail((prev) => {
      if (!prev?.sale || Number(prev.sale.id) !== saleId) return prev;
      return {
        ...prev,
        sale: { ...prev.sale, ...patch },
      };
    });
  };

  const refreshSaleRowInTable = async (saleId) => {
    if (!saleId) return;
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/sales/${saleId}/detail`);
      if (!response.ok) return;
      const payload = await response.json();
      if (payload?.sale) applySaleSnapshot(payload.sale);
    } catch (refreshError) {
      console.error('Error refreshing sale row:', refreshError);
    }
  };

  const refreshOpenSaleDetail = async (saleRow = selectedSale, { silent = true } = {}) => {
    if (!saleRow?.id) return null;
    if (!silent) setIsDetailLoading(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/sales/${saleRow.id}/detail`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      setSaleDetail(payload);
      if (payload?.sale) applySaleSnapshot(payload.sale);
      return payload;
    } catch (detailError) {
      console.error('Error loading sale detail:', detailError);
      if (!silent) {
        setSaleDetail({ sale: saleRow, items: [], deliveries: [], legs: [], trips: [] });
      }
      return null;
    } finally {
      if (!silent) setIsDetailLoading(false);
    }
  };

  const openSaleDetail = async (saleRow) => {
    if (!saleRow?.id) return;
    detailSessionDirtyRef.current = false;
    setSelectedSale(saleRow);
    await refreshOpenSaleDetail(saleRow, { silent: false });
    // El libro de ventas exige permisos del módulo de finanzas: su fallo no debe
    // impedir ver el detalle comercial.
    setIsLoadingDocumentCandidates(true);
    try {
      const candidates = await getSaleDocumentCandidates(saleRow.id);
      setDocumentCandidates(Array.isArray(candidates) ? candidates : []);
      setDocumentCandidatesError('');
    } catch (candidateError) {
      console.error('Error loading sale document candidates:', candidateError);
      setDocumentCandidates([]);
      setDocumentCandidatesError('No se pudo consultar el Libro de Ventas.');
    } finally {
      setIsLoadingDocumentCandidates(false);
    }
  };

  const closeSaleDetail = () => {
    const saleId = Number(selectedSale?.id || saleDetail?.sale?.id || 0);
    const latestSale = saleDetail?.sale;
    const shouldRefreshRow = detailSessionDirtyRef.current;

    if (latestSale?.id) {
      applySaleSnapshot(latestSale);
    }

    setSelectedSale(null);
    setSaleDetail(null);
    setDocumentCandidates([]);
    setDocumentCandidatesError('');
    setIsDetailLoading(false);
    detailSessionDirtyRef.current = false;

    if (shouldRefreshRow && saleId > 0) {
      void refreshSaleRowInTable(saleId);
    }
  };

  const loadDocumentCandidates = async (search = '') => {
    if (!selectedSale?.id) return;
    setIsLoadingDocumentCandidates(true);
    try {
      const candidates = await getSaleDocumentCandidates(selectedSale.id, search);
      setDocumentCandidates(Array.isArray(candidates) ? candidates : []);
      setDocumentCandidatesError('');
    } catch (candidateError) {
      console.error('Error loading sale document candidates:', candidateError);
      setDocumentCandidates([]);
      setDocumentCandidatesError('No se pudo consultar el Libro de Ventas.');
    } finally {
      setIsLoadingDocumentCandidates(false);
    }
  };

  const updateDocumentLink = async (document, shouldLink, { linkMethod = 'manual', itemSelection = {}, isReplacement = false } = {}) => {
    if (!selectedSale?.id || !document?.id) return;
    setIsLinkingDocument(true);
    try {
      await setRegisterSaleLink(document.id, {
        sale_id: shouldLink ? selectedSale.id : null,
        link_method: shouldLink ? linkMethod : 'manual',
        match_score: shouldLink ? Number(document.match_score || 0) : null,
        notes: shouldLink ? buildLinkItemNotes(itemSelection, isReplacement, saleDetail?.items || []) : '',
      });
      detailSessionDirtyRef.current = true;
      await refreshOpenSaleDetail(selectedSale, { silent: true });
      await loadDocumentCandidates();
    } catch (linkError) {
      window.alert(linkError.message || 'No se pudo actualizar el vínculo del documento.');
    } finally {
      setIsLinkingDocument(false);
    }
  };

  const addManualBillingDocument = async (payload) => {
    if (!selectedSale?.id) return;
    setIsLinkingDocument(true);
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/quotations/sales/${selectedSale.id}/billing-documents/manual`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      detailSessionDirtyRef.current = true;
      await refreshOpenSaleDetail(selectedSale, { silent: true });
    } catch (error) {
      window.alert(error.message || 'No se pudo registrar el documento de facturación.');
    } finally {
      setIsLinkingDocument(false);
    }
  };

  const deleteManualBillingDocument = async (docKey) => {
    if (!selectedSale?.id || !docKey) return;
    setIsLinkingDocument(true);
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/quotations/sales/${selectedSale.id}/billing-documents/manual/${encodeURIComponent(docKey)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      detailSessionDirtyRef.current = true;
      await refreshOpenSaleDetail(selectedSale, { silent: true });
    } catch (error) {
      window.alert(error.message || 'No se pudo eliminar el documento manual.');
    } finally {
      setIsLinkingDocument(false);
    }
  };

  const reconcileManualBillingDocument = async (docKey, registerDocumentId) => {
    if (!selectedSale?.id || !docKey || !registerDocumentId) return;
    setIsLinkingDocument(true);
    try {
      const response = await authFetch(
        `${API_BASE_URL}/api/quotations/sales/${selectedSale.id}/billing-documents/manual/${encodeURIComponent(docKey)}/reconcile`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ register_document_id: Number(registerDocumentId) }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      detailSessionDirtyRef.current = true;
      await refreshOpenSaleDetail(selectedSale, { silent: true });
      await loadDocumentCandidates();
    } catch (error) {
      window.alert(error.message || 'No se pudo conciliar el documento manual.');
    } finally {
      setIsLinkingDocument(false);
    }
  };

  const confirmSuggestedDocumentLink = async (document) => {
    const saleItems = saleDetail?.items || [];
    const saleTotal = saleDetail?.sale?.total_amount;
    const amount = Math.abs(getBillingDocumentAmount(document));
    const invoicedItemIds = documentAmountMatchesSaleTotal(amount, saleTotal)
      ? Object.keys(getAllSaleItemSelection(saleItems)).map(Number)
      : (document.invoiced_item_ids || []);
    const itemSelection = documentAmountMatchesSaleTotal(amount, saleTotal)
      ? getAllSaleItemSelection(saleItems)
      : Object.fromEntries(
          (document.invoiced_item_ids || []).map((id) => {
            const item = saleItems.find((row) => Number(row.id) === Number(id));
            const quantities = document.invoiced_item_quantities || {};
            const qty = Number(quantities[String(id)] ?? quantities[id] ?? item?.quantity ?? 0);
            return [Number(id), qty];
          }).filter(([, qty]) => qty > 0)
        );
    await updateDocumentLink(document, true, {
      linkMethod: 'manual',
      itemSelection,
      isReplacement: Boolean(document.is_replacement),
    });
  };

  const saveSaleDetail = async (payload) => {
    if (!selectedSale?.id) return;
    setIsSavingDetail(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/sales/${selectedSale.id}/detail`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const result = await response.json().catch(() => ({}));
      detailSessionDirtyRef.current = true;
      if (result?.sale) {
        applySaleSnapshot(result.sale);
      } else {
        // Fallback mínimo: refleja el payload en la fila abierta.
        applySaleSnapshot({
          id: selectedSale.id,
          ...payload,
          deal_close_date: payload.deal_close_date || null,
          quotation_closing_date: payload.deal_close_date || null,
        });
      }
    } catch (saveError) {
      window.alert(saveError.message || 'No se pudo guardar el detalle de venta.');
    } finally {
      setIsSavingDetail(false);
    }
  };

  const handleAddDelivery = async (deliveryTipo = 'Despacho Completo') => {
    if (!selectedSale?.id) return;
    const tipo = String(deliveryTipo || '').trim();
    const allowedFromSale = ['Despacho Completo', 'Retiro en Bodega'];
    if (!allowedFromSale.includes(tipo)) {
      window.alert(
        'Desde venta solo puedes crear Despacho Completo o Retiro en Bodega. Despacho Parcial y Reposición se gestionan en Despachos → Entregas.'
      );
      return;
    }
    const confirmMsg =
      tipo === 'Retiro en Bodega'
        ? 'Se creará una entrega de Retiro en Bodega (Programada) para esta venta. ¿Continuar?'
        : 'Se creará una entrega de Despacho Completo (Programada) para esta venta. ¿Continuar?';
    if (!window.confirm(confirmMsg)) return;

    setIsAddingDelivery(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const payload = {
        sale_id: Number(selectedSale.id),
        sale_code: selectedSale.sale_code || saleDetail?.sale?.sale_code || '',
        quotation_code: selectedSale.quotation_code || saleDetail?.sale?.quotation_code || '',
        organization_name: selectedSale.organization_name || saleDetail?.sale?.organization_name || '',
        delivery_type: tipo === 'Retiro en Bodega' ? 'Retiro Bodega' : 'Despacho',
        delivery_status: 'Programada',
        delivery_kind: tipo,
        weight_kg: 0,
        confirm_reposicion: false,
        from_sale: true,
        scheduled_date: today,
        delivered_date: '',
        dispatch_guide: saleDetail?.sale?.dispatch_guide || '',
        delivery_address:
          saleDetail?.sale?.delivery_address ||
          saleDetail?.sale?.delivery_place ||
          selectedSale.delivery_place ||
          '',
        pickup_warehouse: '',
        receiver_name: saleDetail?.sale?.receiver_name || '',
        notes: '',
      };
      const response = await authFetch(`${API_BASE_URL}/api/quotations/logistics/deliveries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      detailSessionDirtyRef.current = true;
      await refreshOpenSaleDetail(selectedSale, { silent: true });
    } catch (createError) {
      window.alert(createError.message || 'No se pudo crear la entrega.');
    } finally {
      setIsAddingDelivery(false);
    }
  };

  const quotationOptions = useMemo(() => {
    const currentQid = Number(saleDetail?.sale?.quotation_id || selectedSale?.quotation_id || 0);
    const usedByOther = new Set(
      (salesData || [])
        .filter((s) => Number(s.id) !== Number(selectedSale?.id))
        .map((s) => Number(s.quotation_id))
        .filter(Boolean)
    );
    const list = (quotationsData || [])
      .filter((q) => {
        const id = Number(q.id);
        if (!id) return false;
        if (id === currentQid) return true;
        if (usedByOther.has(id)) return false;
        const status = String(q.status || '').trim();
        return status === 'Vendida' || status === 'Emitida' || status === 'Adjudicada' || id === currentQid;
      })
      .map((q) => ({
        value: String(q.id),
        label: `${q.quotation_code || `Q-${q.id}`} · ${q.organization_name || 'Sin cliente'} · ${q.status || ''}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));

    if (currentQid && !list.some((o) => Number(o.value) === currentQid)) {
      list.unshift({
        value: String(currentQid),
        label: `${saleDetail?.sale?.quotation_code || selectedSale?.quotation_code || `Q-${currentQid}`} (actual)`,
      });
    }
    return list;
  }, [quotationsData, salesData, selectedSale, saleDetail]);

  const saleDateValues = useMemo(
    () => collectDates(salesData, getSaleAnchorDate),
    [salesData]
  );

  const availableYears = useMemo(
    () => getAvailableYearsFromDates(saleDateValues),
    [saleDateValues]
  );

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues: saleDateValues,
        secondaryYear,
        secondaryMonth,
        titles: {
          monthly: 'Resumen ventas mensual',
          annual: 'Resumen ventas anual',
        },
      }),
    [periodFilter, saleDateValues, secondaryYear, secondaryMonth]
  );

  const salesPeriod = useMemo(
    () => withSalesPeriodFutureCloseBuffer(selectedPeriod, periodFilter),
    [selectedPeriod, periodFilter]
  );

  useEffect(() => {
    if (!availableYears.length) return;
    if (periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral') {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly') {
      if (!availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear(String(availableYears[availableYears.length - 1]));
      }
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (nextPeriod === 'annual' || nextPeriod === 'annual_ytd' || nextPeriod === 'quarterly' || nextPeriod === 'semestral') {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear));
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const filteredSalesData = useMemo(
    () => salesData.filter((sale) => isDateInSelectedPeriod(getSaleAnchorDate(sale), salesPeriod)),
    [salesData, salesPeriod]
  );

  const salesListRows = useMemo(
    () =>
      filteredSalesData.filter(
        (sale) => String(sale.quotation_status || '').trim() !== 'Adjudicada'
      ),
    [filteredSalesData]
  );

  const adjudicatedQuotationsWithoutSale = useMemo(() => {
    const saleByQuotationId = new Map(
      salesData
        .map((sale) => [Number(sale.quotation_id), sale])
        .filter(([quotationId]) => quotationId > 0)
    );
    return (quotationsData || []).filter((quotation) => {
      if (quotation?.is_active === false) return false;
      if (normalizeQuotationStatus(quotation.status) !== 'Adjudicada') return false;
      const sale = saleByQuotationId.get(Number(quotation.id));
      if (!sale) return true;
      if (String(sale.quotation_status || '').trim() === 'Vendida') return false;
      return !String(sale.sale_code || '').trim();
    });
  }, [quotationsData, salesData]);

  const filteredAdjudicatedQuotations = useMemo(
    () =>
      adjudicatedQuotationsWithoutSale.filter((quotation) =>
        isDateInSelectedPeriod(getQuotationAnchorDate(quotation), salesPeriod)
      ),
    [adjudicatedQuotationsWithoutSale, salesPeriod]
  );

  const listTabs = useMemo(
    () => [
      { key: 'sales', label: 'Listado Ventas', count: salesListRows.length },
      {
        key: 'adjudicated',
        label: 'Adjudicadas',
        count: adjudicatedQuotationsWithoutSale.length,
      },
    ],
    [salesListRows.length, adjudicatedQuotationsWithoutSale.length]
  );

  const listSubtitle = isLoading
    ? 'Cargando…'
    : listTab === 'adjudicated'
      ? error ||
        `${filteredAdjudicatedQuotations.length} cotizaciones adjudicadas sin venta`
      : error || `${salesListRows.length} ventas`;

  const saleSegmentById = useMemo(() => {
    const map = new Map();
    salesData.forEach((sale) => {
      map.set(Number(sale.id), normalizeCommercialSegment(sale.organization_segment));
    });
    return map;
  }, [salesData]);

  const summarySegmentOptions = useMemo(() => {
    const discovered = new Set();
    saleSegmentById.forEach((segment) => discovered.add(segment));
    return [
      ...SUMMARY_SEGMENT_ORDER.filter((key) => discovered.has(key)),
      ...[...discovered].filter((key) => !SUMMARY_SEGMENT_ORDER.includes(key)).sort((a, b) => a.localeCompare(b, 'es')),
    ];
  }, [saleSegmentById]);

  useEffect(() => {
    if (!summarySegmentOptions.length) return;
    setSummarySegmentVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      summarySegmentOptions.forEach((segment) => {
        if (next[segment] === undefined) {
          next[segment] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [summarySegmentOptions]);

  const handleToggleSummarySegment = (segment) => {
    setSummarySegmentVisibility((prev) => ({
      ...prev,
      [segment]: prev[segment] === false,
    }));
  };

  const handleShowAllSummarySegments = () => {
    setSummarySegmentVisibility(
      summarySegmentOptions.reduce((acc, segment) => {
        acc[segment] = true;
        return acc;
      }, {})
    );
  };

  const handleHideAllSummarySegments = () => {
    setSummarySegmentVisibility(
      summarySegmentOptions.reduce((acc, segment) => {
        acc[segment] = false;
        return acc;
      }, {})
    );
  };

  const tcoatGallonsByQuotationId = useMemo(() => {
    const map = new Map();
    const saleQuotationIds = new Set(
      salesData.map((sale) => Number(sale.quotation_id)).filter(Boolean)
    );
    (quotationItemsData || []).forEach((item) => {
      const quotationId = Number(item.quotation_id);
      if (!quotationId || !saleQuotationIds.has(quotationId)) return;
      if (!isTcoatQuotationItem(item, paintSkuSet, upsellingSkuSet)) return;
      const gallons = resolveTcoatLineGallons(item);
      if (gallons <= 0) return;
      map.set(quotationId, (map.get(quotationId) || 0) + gallons);
    });
    return map;
  }, [quotationItemsData, salesData, paintSkuSet, upsellingSkuSet]);

  const saleRegionChartItems = useMemo(() => {
    const saleByQuotationId = new Map(
      salesData
        .filter((sale) => Number(sale.quotation_id))
        .map((sale) => [Number(sale.quotation_id), sale])
    );
    const quotedSaleIds = new Set();

    const buildMeta = (sale) => ({
      region: sale.organization_region || '',
      segment: sale.organization_segment || '',
      status: resolveChartSaleStatus(sale),
      executive: (sale.user_name || '').trim() || 'Sin ejecutivo',
      date: getSaleAnchorDate(sale),
      saleId: Number(sale.id),
      quotationId: Number(sale.quotation_id) || null,
      quotationAmount: isClpLikeCurrency(sale.currency)
        ? grossAmountConIva(sale.total_amount)
        : 0,
    });

    const lineItems = (quotationItemsData || []).flatMap((item) => {
      const sale = saleByQuotationId.get(Number(item.quotation_id));
      if (!sale) return [];
      quotedSaleIds.add(Number(sale.id));
      const isTcoat = isTcoatQuotationItem(item, paintSkuSet, upsellingSkuSet);
      const lineNet = resolveQuotationLineNet(item);
      const gallons = isTcoat ? resolveTcoatLineGallons(item) : 0;
      const discount = Math.max(Math.min(Number(item.discount_percent || 0), 100), 0);
      const netUnitPrice = Number(item.unit_price || 0) * (1 - discount / 100);
      const sku = String(item.product_sku || '').trim();
      const name = String(item.item_name || item.product_name || '').trim();
      return [
        {
          ...buildMeta(sale),
          productLine: isTcoat ? 'T-COAT' : 'Upselling',
          productName: name || 'Sin producto',
          tcoatProduct: isTcoat ? [sku, name].filter(Boolean).join(' · ') || 'Sin producto' : 'No T-COAT',
          amount: isClpLikeCurrency(sale.currency) ? grossAmountConIva(lineNet) : 0,
          gallons,
          pricePerGallon: gallons > 0 ? resolvePricePerGallon(netUnitPrice, item.unit) : 0,
        },
      ];
    });

    const emptySaleItems = salesData.flatMap((sale) => {
      const id = Number(sale.id);
      if (!id || quotedSaleIds.has(id)) return [];
      return [
        {
          ...buildMeta(sale),
          productLine: 'Sin ítems',
          productName: 'Sin producto',
          tcoatProduct: 'No T-COAT',
          amount: 0,
          gallons: 0,
          pricePerGallon: 0,
          isEmptyQuote: true,
        },
      ];
    });

    return [...lineItems, ...emptySaleItems];
  }, [salesData, quotationItemsData, paintSkuSet, upsellingSkuSet]);

  const saleChartStackDimensions = useMemo(
    () => [
      SEGMENT_STACK_DIMENSION,
      REGION_FILTER_DIMENSION,
      {
        key: 'status',
        label: 'Estado',
        order: SALE_CHART_STATUS_ORDER,
        colors: SALE_STATUS_CHART_COLORS,
        fallback: 'En Preparación',
      },
      {
        key: 'executive',
        label: 'Ejecutivo',
        fallback: 'Sin ejecutivo',
      },
      {
        ...PRODUCT_LINE_DIMENSION,
        label: 'T-COAT / Upselling',
      },
    ],
    []
  );
  const saleMonthlyStackDimensions = useMemo(
    () => [...saleChartStackDimensions, TCOAT_PRODUCT_DIMENSION],
    [saleChartStackDimensions]
  );

  const saleChartItems = useMemo(() => {
    const range = salesPeriod?.range;
    if (!range) return saleRegionChartItems;
    return saleRegionChartItems.filter((item) => isDateInRange(item.date, range));
  }, [saleRegionChartItems, salesPeriod]);

  const saleChartPeriods = useMemo(() => {
    if (salesPeriod?.mode === 'year') {
      return salesPeriod.columns || [];
    }
    const range = salesPeriod?.range;
    if (range?.start && range?.end) {
      return getMonthsInRange(range.start, range.end);
    }
    return salesPeriod?.columns || [];
  }, [salesPeriod]);

  const salesSummaryRows = useMemo(() => {
    const periods = salesPeriod.columns || [];
    const periodStart = periods[0]?.start || salesPeriod.range?.start || null;
    const isSegmentVisible = (sale) => {
      const segment = saleSegmentById.get(Number(sale.id)) || 'Sin Asignar';
      return summarySegmentVisibility[segment] !== false;
    };
    const scopedSales = salesData.filter(isSegmentVisible);

    const inBucket = (sale, bucket) => {
      const date = parseDateSafe(getSaleAnchorDate(sale));
      return Boolean(date && date >= bucket.start && date <= bucket.end);
    };
    const upToBucket = (sale, bucket) => {
      const date = parseDateSafe(getSaleAnchorDate(sale));
      if (!date || date > bucket.end) return false;
      if (periodStart && date < periodStart) return false;
      return true;
    };

    const countBy = (predicate) =>
      periods.map((bucket) => scopedSales.filter((s) => inBucket(s, bucket) && predicate(s)).length);
    const cumCountBy = (predicate) =>
      periods.map((bucket) => scopedSales.filter((s) => upToBucket(s, bucket) && predicate(s)).length);
    const matchesVendidaStatus = (sale, status) =>
      isVendidaSale(sale) && normalizeSaleStatus(sale.sale_status) === status;

    const statusCountRows = SALE_STATUS_ORDER.map((status) => ({
      key: `status-count-${status}`,
      label: status,
      tone: 'neutral',
      indent: 1,
      values: countBy((sale) => matchesVendidaStatus(sale, status)).map(formatSummaryCount),
    }));

    if (summaryMetric === 'amount') {
      const sumClpBy = (predicate) =>
        periods.map((bucket) =>
          scopedSales
            .filter((s) => inBucket(s, bucket) && isClpLikeCurrency(s.currency) && predicate(s))
            .reduce((acc, s) => acc + grossAmountConIva(s.total_amount), 0)
        );
      const cumSumClpBy = (predicate) =>
        periods.map((bucket) =>
          scopedSales
            .filter((s) => upToBucket(s, bucket) && isClpLikeCurrency(s.currency) && predicate(s))
            .reduce((acc, s) => acc + grossAmountConIva(s.total_amount), 0)
        );
      const statusAmountRows = SALE_STATUS_ORDER.map((status) => ({
        key: `status-amount-${status}`,
        label: status,
        tone: 'neutral',
        indent: 1,
        values: sumClpBy((sale) => matchesVendidaStatus(sale, status)).map(formatSummaryMoney),
      }));
      return [
        {
          key: 'amount-vendidas',
          label: 'Monto vendido c/IVA (CLP)',
          tone: 'client',
          values: sumClpBy(isVendidaSale).map(formatSummaryMoney),
        },
        {
          key: 'amount-adjudicadas',
          label: 'Monto adjudicado c/IVA (CLP)',
          tone: 'prospect',
          values: sumClpBy(isAdjudicadaSale).map(formatSummaryMoney),
        },
        {
          key: 'amount-vendidas-acum',
          label: 'Monto vendido acum. c/IVA (CLP)',
          tone: 'total',
          values: cumSumClpBy(isVendidaSale).map(formatSummaryMoney),
        },
        ...statusAmountRows,
      ];
    }

    if (summaryMetric === 'gallons') {
      const gallonsOf = (sale) => tcoatGallonsByQuotationId.get(Number(sale.quotation_id)) || 0;
      const sumGalBy = (predicate) =>
        periods.map((bucket) =>
          scopedSales
            .filter((s) => inBucket(s, bucket) && predicate(s))
            .reduce((acc, s) => acc + gallonsOf(s), 0)
        );
      const cumGalBy = (predicate) =>
        periods.map((bucket) =>
          scopedSales
            .filter((s) => upToBucket(s, bucket) && predicate(s))
            .reduce((acc, s) => acc + gallonsOf(s), 0)
        );
      const formatGal = (value) =>
        (Math.round(Number(value || 0) * 10) / 10).toLocaleString('es-CL', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        });
      const statusGallonRows = SALE_STATUS_ORDER.map((status) => ({
        key: `status-gallons-${status}`,
        label: status,
        tone: 'neutral',
        indent: 1,
        values: sumGalBy((sale) => matchesVendidaStatus(sale, status)).map(formatGal),
      }));
      return [
        {
          key: 'gallons-vendidas',
          label: 'Galones T-COAT vendidos',
          tone: 'client',
          values: sumGalBy(isVendidaSale).map(formatGal),
        },
        {
          key: 'gallons-adjudicadas',
          label: 'Galones T-COAT adjudicados',
          tone: 'prospect',
          values: sumGalBy(isAdjudicadaSale).map(formatGal),
        },
        {
          key: 'gallons-vendidas-acum',
          label: 'Galones vendidos acum. período',
          tone: 'total',
          values: cumGalBy(isVendidaSale).map(formatGal),
        },
        ...statusGallonRows,
      ];
    }

    return [
      {
        key: 'count-vendidas',
        label: 'Vendidas',
        tone: 'client',
        values: countBy(isVendidaSale).map(formatSummaryCount),
      },
      {
        key: 'count-adjudicadas',
        label: 'Adjudicadas',
        tone: 'prospect',
        values: countBy(isAdjudicadaSale).map(formatSummaryCount),
      },
      {
        key: 'count-vendidas-acum',
        label: 'Vendidas acum. período',
        tone: 'total',
        values: cumCountBy(isVendidaSale).map(formatSummaryCount),
      },
      ...statusCountRows,
    ];
  }, [salesData, salesPeriod, summaryMetric, summarySegmentVisibility, saleSegmentById, tcoatGallonsByQuotationId]);

  const periodTitle =
    salesPeriod?.title ||
    (periodFilter === 'all'
      ? 'Resumen ventas · todos los períodos'
      : 'Resumen ventas por período de cierre');

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'start',
          columnGap: '16px',
          rowGap: '12px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>
            <Activity size={22} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Ventas
          </Heading>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            gap: '10px',
            flexWrap: 'nowrap',
            marginTop: '22px',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: '180px' }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '110px' }}
              >
                <option value="all">Todas</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={String(
                    secondaryYear === 'all'
                      ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                      : secondaryYear
                  )}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: '10px',
          overflow: chartsCollapsed ? 'hidden' : 'visible',
          margin: '10px 0 12px',
          background: ui.panel,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '7px 10px',
            borderBottom: chartsCollapsed ? 'none' : `1px solid ${ui.border}`,
            background: ui.header,
            borderRadius: chartsCollapsed ? '10px' : '10px 10px 0 0',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '13px', color: ui.text, fontWeight: 700, lineHeight: 1.2 }}>
            Gráficos
            {isItemsLoading ? (
              <span style={{ marginLeft: 8, fontWeight: 500, color: ui.muted, fontSize: 11 }}>
                Cargando detalle de ítems…
              </span>
            ) : null}
          </h3>
          <button
            type="button"
            onClick={() => setChartsCollapsed((prev) => !prev)}
            aria-expanded={!chartsCollapsed}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              border: `1px solid ${ui.border}`,
              borderRadius: '6px',
              background: ui.panel,
              color: ui.text,
              fontSize: '11px',
              fontWeight: 600,
              padding: '3px 8px',
              cursor: 'pointer',
              lineHeight: 1.2,
              height: '28px',
            }}
          >
            {chartsCollapsed ? 'Mostrar' : 'Ocultar'}
            {chartsCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
        {!chartsCollapsed ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '12px',
              alignItems: 'start',
              overflow: 'visible',
              padding: '0 10px 4px',
            }}
          >
            <RegionSegmentStackedChart
              items={saleChartItems}
              title="Distribución de ventas"
              totalLabel="ventas"
              stackDimensions={saleChartStackDimensions}
              defaultStackKey="segment"
              filterOnlyDimensions={SALE_CHART_PRODUCT_FILTERS}
              valueMetrics={SALE_CHART_VALUE_METRICS}
            />
            <MonthlyDistributionChart
              periods={saleChartPeriods}
              items={saleChartItems}
              title="Distribución de ventas"
              totalLabel="ventas"
              stackDimensions={saleMonthlyStackDimensions}
              defaultStackKey="productLine"
              defaultMetricKey="amount"
              filterOnlyDimensions={SALE_CHART_PRODUCT_FILTERS}
              valueMetrics={SALE_CHART_VALUE_METRICS}
            />
          </div>
        ) : null}
      </div>

      <PeriodSummaryTable
        periods={salesPeriod.columns}
        rows={salesSummaryRows}
        title={periodTitle}
        collapsed={summaryCollapsed}
        onToggleCollapsed={() => setSummaryCollapsed((prev) => !prev)}
        segmentOptions={summarySegmentOptions}
        segmentVisibility={summarySegmentVisibility}
        onToggleSegment={handleToggleSummarySegment}
        onShowAllSegments={handleShowAllSummarySegments}
        onHideAllSegments={handleHideAllSummarySegments}
        metricOptions={SUMMARY_METRIC_OPTIONS}
        metric={summaryMetric}
        onMetricChange={setSummaryMetric}
      />

      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: 12,
          overflow: 'hidden',
          background: ui.page,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: ui.text }}>Ventas</h3>
            <p style={{ margin: 0, fontSize: 12, color: ui.muted }}>{listSubtitle}</p>
          </div>
        </div>

        <div
          role="tablist"
          aria-label="Vistas de ventas"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '0 12px',
            borderBottom: `1px solid ${ui.border}`,
            background: ui.header,
            overflowX: 'auto',
          }}
        >
          {listTabs.map((item) => {
            const active = listTab === item.key;
            return (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setListTab(item.key)}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '10px 12px',
                  marginBottom: -1,
                  fontSize: 13,
                  fontWeight: active ? 700 : 500,
                  color: active ? ui.text : ui.muted,
                  borderBottom: active ? '2px solid #16a34a' : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span>{item.label}</span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: active ? (isDarkMode ? '#86efac' : '#15803d') : ui.muted,
                    background: active
                      ? isDarkMode
                        ? 'rgba(22, 163, 74, 0.2)'
                        : 'rgba(22, 163, 74, 0.12)'
                      : isDarkMode
                        ? 'rgba(255,255,255,0.06)'
                        : 'rgba(0,0,0,0.05)',
                    borderRadius: 999,
                    padding: '1px 7px',
                    minWidth: 18,
                    textAlign: 'center',
                  }}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>

        {listTab === 'adjudicated' ? (
          <QuotationsTable3
            rows={filteredAdjudicatedQuotations}
            loading={isLoading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            gallonsByQuotationId={tcoatGallonsByQuotationId}
          />
        ) : (
          <SalesTable3
            rows={salesListRows}
            loading={isLoading}
            error={error}
            isDarkMode={isDarkMode}
            hideChrome
            onOpenSale={openSaleDetail}
            gallonsByQuotationId={tcoatGallonsByQuotationId}
          />
        )}
      </div>
      <SalesDetailModal
        isOpen={Boolean(selectedSale)}
        onClose={closeSaleDetail}
        isDarkMode={isDarkMode}
        detail={isDetailLoading ? { sale: selectedSale, items: [], deliveries: [], legs: [], trips: [] } : saleDetail}
        onSave={saveSaleDetail}
        isSaving={isSavingDetail}
        documentCandidates={documentCandidates}
        documentCandidatesError={documentCandidatesError}
        isLoadingDocumentCandidates={isLoadingDocumentCandidates}
        isLinkingDocument={isLinkingDocument}
        onSearchDocuments={loadDocumentCandidates}
        onSetDocumentLink={updateDocumentLink}
        onConfirmDocumentLink={confirmSuggestedDocumentLink}
        onAddManualBillingDocument={addManualBillingDocument}
        onDeleteManualBillingDocument={deleteManualBillingDocument}
        onReconcileManualBillingDocument={reconcileManualBillingDocument}
        quotationOptions={quotationOptions}
        onAddDelivery={handleAddDelivery}
        isAddingDelivery={isAddingDelivery}
      />
    </div>
  );
};

export default PaintSalesWorkspace;
